import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';
import { useUpdateTotalNotification } from '../../utils/useUpdateTotalNotification';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { useHistory } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import CircleImage from '../../components/CircleImage';
import DisplayDateTime from '../../components/DisplayDateTime';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';

// ICONS
import { ReactComponent as ArrowLeftIcon } from '../../assets/svg/chevron-left.svg';
import { ReactComponent as EnvelopeIcon } from '../../assets/svg/envelope-icon.svg';

// TYPES
import { IncidentArchiveEditUser, DomainIncidentArchiveType } from '../../api/models';
import { IncidentFormTypes } from './IncidentSubmit';

interface StepThreeTypes {
	incidentFormData: IncidentFormTypes;
	setIncidentFormData: React.Dispatch<React.SetStateAction<IncidentFormTypes>>;
	setActiveStep: React.Dispatch<React.SetStateAction<string>>;

	leadershipTeamData: IncidentArchiveEditUser[];
	otherRecipientsData: IncidentArchiveEditUser[];

	isLoading: boolean;
	setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

const StepThree: React.FC<StepThreeTypes> = ({
	incidentFormData,
	setIncidentFormData,
	setActiveStep,

	leadershipTeamData,
	otherRecipientsData,

	isLoading,
	setIsLoading
}) => {
	const history = useHistory();

	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Get all incident Types from Redux store
	const incidentArchiveTypes = useAppSelector((state) => state.lists.incidentArchiveTypes);

	// Error state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const {
		title,
		occurrence,
		type,
		location,
		impacted,
		wasWitnessedFirstHand,
		wasAuthorityAlerted,
		notifiedAuthorities,
		notified,
		description,
		actions,
		attachments,
		newAttachments,
		deleteAttachmentIds,
		otherRecipientEmails
	} = incidentFormData;

	const createIncidentReport = async () => {
		if (incidentFormData !== undefined) {
			setIsLoading(true);

			const response = await apibridge.createIncidentReport(incidentFormData);

			if (response.isError) {
				setIsLoading(false);

				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				setIsLoading(false);

				history.push('/incident-reports');

				useUpdateTotalNotification();
			}
		}
	};

	const displayIncidentType = (type?: DomainIncidentArchiveType) => {
		if (type) {
			const currentType = incidentArchiveTypes?.find((item) => item.value === type);
			return currentType?.label;
		}
	};

	return (
		<div>
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								<h2 className="general-header">Please review your incident report before submitting it</h2>

								<p className="sub-grey-text mbtom-48">
									The nominated contacts will be notified of this incident report and asked to acknowledge it.
								</p>

								<div className="general-form-layout-wrapper">
									<div className="complaints-detail">
										<div className="detail-block">
											<h2 className="detail-heading">Incident title</h2>

											<p className="detail-info">{title}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Date and time of incident</h2>

											<time className="detail-info">
												<DisplayDateTime>{occurrence}</DisplayDateTime>
											</time>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Incident type</h2>

											<p className="detail-info">{displayIncidentType(type)}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Where did the incident happen?</h2>

											<p className="detail-info">{location}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Who was involved in this incident?</h2>

											<p className="detail-info">{impacted}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Did you witness the incident first hand?</h2>

											<p className="detail-info">{wasWitnessedFirstHand ? 'Yes' : 'No'}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Was an authority alerted?</h2>

											<p className="detail-info">{wasAuthorityAlerted ? 'Yes' : 'No'}</p>
										</div>

										{notifiedAuthorities && (
											<div className="detail-block">
												<h2 className="detail-heading">
													Name, organisation, position and contact details of notified authority
												</h2>

												<p className="detail-info">{notifiedAuthorities}</p>
											</div>
										)}

										<div className="detail-block">
											<h2 className="detail-heading">Date and time authority was notified</h2>

											<time className="detail-info">
												<DisplayDateTime>{notified}</DisplayDateTime>
											</time>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">Incident description</h2>

											<p className="detail-info">{description}</p>
										</div>

										<div className="detail-block">
											<h2 className="detail-heading">What were the actions taken?</h2>

											<p className="detail-info">{actions}</p>
										</div>

										{attachments && attachments.length > 0 && (
											<div className="detail-block">
												<h2 className="detail-heading">Attachments</h2>

												<ul>
													{attachments &&
														attachments.length > 0 &&
														attachments
															.filter((item) => !deleteAttachmentIds?.includes(item.id || ''))
															.map((item, index) => {
																return (
																	<li key={index}>
																		<div className="attachments-download-link">
																			<span className="link-name">{item.displayFileName}</span>
																		</div>
																	</li>
																);
															})}

													{newAttachments &&
														newAttachments.length > 0 &&
														newAttachments.map((item, index) => {
															return (
																<li key={index}>
																	<div className="attachments-download-link">
																		<span className="link-name">{item.displayFileName}</span>
																	</div>
																</li>
															);
														})}
												</ul>
											</div>
										)}

										<div className="detail-block">
											<h2 className="detail-heading mbtom-14">Incident report notified to:</h2>
										</div>
									</div>
								</div>

								<section className="info-section w-50 mtop-0">
									<ul>
										{leadershipTeamData &&
											leadershipTeamData.length > 0 &&
											leadershipTeamData.map((item) => {
												if (
													item.id &&
													incidentFormData.leaderRecipientIds &&
													incidentFormData.leaderRecipientIds.includes(item.id.toString())
												) {
													return (
														<li key={item.id} className="row-container">
															<div className="row detail-row no-border">
																<div className="col-2 pdl-0">
																	<CircleImage firstName={item.name} lastName={item.name} src={item.profileImage} />
																</div>

																<div className="col-10">
																	<div className="row detail-info">
																		<div className="col-lg-8 detail-name">{item.name}</div>

																		<div className="col-lg-4 detail-status">
																			<span className="label">{item.role}</span>
																		</div>
																	</div>
																</div>
															</div>
														</li>
													);
												} else {
													return null;
												}
											})}
									</ul>

									<ul>
										{otherRecipientsData &&
											otherRecipientsData.length > 0 &&
											otherRecipientsData.map((item) => {
												if (
													item.id &&
													incidentFormData.otherRecipientIds &&
													incidentFormData.otherRecipientIds.includes(item.id.toString())
												) {
													return (
														<li key={item.id} className="row-container">
															<div className="row detail-row no-border">
																<div className="col-2 pdl-0">
																	<CircleImage firstName={item.name} lastName={item.name} src={item.profileImage} />
																</div>

																<div className="col-10">
																	<div className="row detail-info">
																		<div className="col-lg-8 detail-name">{item.name}</div>

																		<div className="col-lg-4 detail-status">
																			<span className="label">{item.role}</span>
																		</div>
																	</div>
																</div>
															</div>
														</li>
													);
												} else {
													return null;
												}
											})}
									</ul>

									<ul>
										{otherRecipientEmails &&
											otherRecipientEmails.length > 0 &&
											otherRecipientEmails.map((item, index) => {
												return (
													<li key={index} className="row-container">
														<div className="row detail-row no-border">
															<div className="col-2 pdl-0">
																<EnvelopeIcon />
															</div>

															<div className="col-10">
																<div className="row detail-info">
																	<div className="col-12 detail-name">{item}</div>
																</div>
															</div>
														</div>
													</li>
												);
											})}
									</ul>
								</section>

								<div className="general-divider"></div>

								<div className="form-two-button-group">
									<Button
										disabled={isLoading}
										buttonIcon={<ArrowLeftIcon className="link-icon" />}
										className="btn-secondary edit-btn"
										onClick={() => setActiveStep('step1')}
									>
										Edit details
									</Button>

									{isOrganisationActive && (
										<Button disabled={isLoading} className="btn-primary submit-btn" onClick={createIncidentReport}>
											Submit report
										</Button>
									)}
								</div>
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default StepThree;
